import React, { useState, useEffect } from "react";
import {
  TextField,
  Typography,
  Button,
  Box,
  InputAdornment,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { baseapi, frontbaseapi } from "../Environmentalurl";
import { entity_id, Platform, getGeolocation, location } from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";
import { setLoginMeta } from "../Slice/authSlice";
import { useMediaQuery } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import api from "../Utils/Api";
import axios from "axios";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const VALIDATE_TOKEN_URL =
  `${baseapi}/auth/validatetoken`;

const WHY = [
  "Instant bill payments across 500+ billers",
  "RuPay card with contactless payments",
  "Bank-grade 256-bit SSL encryption",
  "RBI compliant & BBPS certified",
];

const isExpired = (expiryTimeStr) => {
  try {
    const [datePart, timePart] = expiryTimeStr.split(" ");
    const [day, month, year] = datePart.split("-").map(Number);
    const expiryDate = new Date(
      year,
      month - 1,
      day,
      ...(timePart ? timePart.split(":").map(Number) : [0, 0, 0]),
    );
    return Date.now() > expiryDate.getTime();
  } catch {
    return true;
  }
};

const Loginpage = () => {
  const [phone, setPhone] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [initState, setInitState] = useState("checking");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ip = useIPAddress();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    const init = async () => {
      // 1. Already fully logged in → dashboard
      if (sessionStorage.getItem("SessionID")) {
        navigate("/dashboard", { replace: true });
        return;
      }

      // 2. Check localStorage for a saved WalletToken
      const walletToken = localStorage.getItem("SessionWalletToken");

      if (walletToken) {
        try {
          const res = await axios.post(VALIDATE_TOKEN_URL, {
            token: walletToken,
          });
          const data = res.data;

          if (data?.status === "VALID" && !isExpired(data?.expiryTime)) {
            // ✅ Extract mobileNumber directly from the validate response
            const mobileFromToken = data.mobileNumber;

            // Persist it so OtpPage (TPIN screen) can use it
            if (mobileFromToken) {
              sessionStorage.setItem("mobileNumber", mobileFromToken);
            }

            // ✅ FIX: don't rely on route `state` alone to tell OtpPage the
            // token was just validated as VALID. React Router `state` can
            // be lost across certain navigation/remount sequences, and when
            // that happens OtpPage falls back to re-checking
            // localStorage — which re-runs the SAME validatetoken call a
            // second time. If anything about that second call's timing or
            // result differs even slightly, OtpPage can end up routing to
            // the OTP screen instead of TPIN, even though the token is
            // genuinely valid. Writing this flag to sessionStorage makes
            // the "token is valid, go straight to TPIN" decision durable
            // and explicit, independent of route state.
            sessionStorage.setItem("walletTokenValidated", "true");

            // Navigate to verify-otp passing mobile + token in route state
            // OtpPage will read this and skip straight to the TPIN screen
            navigate("/verify-otp", {
              replace: true,
              state: { mobileFromToken, walletToken },
            });
            return;
          }
        } catch {
          // Token validation failed → fall through to login form
        }
      }

      // 3. No valid token → show the login form
      setInitState("ready");
    };

    init();
  }, []);

  useEffect(() => {
    if (cooldown === 0) return;
    const t = setInterval(() => setCooldown((p) => p - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (phone.length !== 10 || !/^[6-9]/.test(phone)) {
      setError(true);
      setSnackbar({
        open: true,
        message: "Enter a valid mobile number",
        severity: "error",
      });
      return;
    }
    setError(false);
    setLoading(true);

    try {
      const payload = {
        ReqType: "One_userlogin",
        MobileNumber: phone,
        EntityID: entity_id,
        SSOID: "-",
        MemberID: "26384597149",
        Statement: "LoginWithOTP",
        MachineID: ip,
        Platform,
        Geolocation: location,
      };

      const response = await api.post(
        `${baseapi}/auth/walletexistance`,
        payload,
      );
      const { Response_Code, Message, MessageRefID } = response.data;

      if (Response_Code === "1110") {
        sessionStorage.setItem("mobileNumber", phone);
        dispatch(
          setLoginMeta({
            isLoggedIn: true,
            messageRefId: MessageRefID,
            mobileNumber: phone,
          }),
        );
        setSnackbar({
          open: true,
          message: "OTP sent successfully!",
          severity: "success",
        });
        setCooldown(30);
        setTimeout(() => navigate("/verify-otp", { replace: true }), 1500);
      } else {
        setSnackbar({
          open: true,
          message: Message || "Invalid mobile number.",
          severity: "error",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Error sending OTP. Try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (initState === "checking") return null;

  return (
    <>
      <Box sx={{ minHeight: "100vh", display: "flex" }}>
        {!isMobile && (
          <Box
            sx={{
              width: "48%",
              flexShrink: 0,
              background:
                "linear-gradient(160deg, #064e3b 0%, #065f46 30%, #16a34a 70%, #22c55e 100%)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: 8,
              py: 6,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -80,
                right: -80,
                width: 320,
                height: 320,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.05)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -60,
                left: -40,
                width: 240,
                height: 240,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.06)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "40%",
                right: 40,
                width: 100,
                height: 100,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.04)",
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 6 }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    bgcolor: "rgba(255,255,255,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src="/Assets/ta_walletlogo.png"
                    sx={{ height: 26, filter: "brightness(0) invert(1)" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 800,
                      color: "white",
                      lineHeight: 1,
                    }}
                  >
                    TA Wallet
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "rgba(255,255,255,0.7)",
                      fontWeight: 500,
                    }}
                  >
                    One App · All Payments
                  </Typography>
                </Box>
              </Box>

              <Typography
                sx={{
                  fontSize: 40,
                  fontWeight: 900,
                  color: "white",
                  lineHeight: 1.1,
                  mb: 2,
                  letterSpacing: "-1.5px",
                }}
              >
                Welcome
                <br />
                back.
              </Typography>
              <Typography
                sx={{
                  fontSize: 15,
                  color: "rgba(255,255,255,0.75)",
                  mb: 6,
                  lineHeight: 1.75,
                }}
              >
                Your trusted digital wallet is waiting. Sign in to manage
                payments, bills, and your RuPay card.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {WHY.map((w, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                  >
                    <CheckCircleIcon
                      sx={{ fontSize: 18, color: "#4ade80", flexShrink: 0 }}
                    />
                    <Typography
                      sx={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}
                    >
                      {w}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box
                component="img"
                src="/Assets/loginimg.png"
                alt="Wallet illustration"
                sx={{
                  width: "100%",
                  maxWidth: 280,
                  mt: 6,
                  objectFit: "contain",
                  filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.2))",
                  mx: "auto",
                  display: "block",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </Box>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f8fafc",
            p: { xs: 3, md: 6 },
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 420 }}>
            {isMobile && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 5 }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #16a34a, #22c55e)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src="/Assets/ta_walletlogo.png"
                    sx={{ height: 22, filter: "brightness(0) invert(1)" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </Box>
                <Typography
                  sx={{ fontWeight: 800, fontSize: 18, color: "#0f172a" }}
                >
                  TA Wallet
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                bgcolor: "white",
                borderRadius: "24px",
                p: { xs: 3.5, md: 4.5 },
                boxShadow: "0 8px 40px rgba(0,0,0,0.06)",
                border: "1.5px solid #f1f5f9",
              }}
            >
              <Box mb={1}>
                <Box
                  component="img"
                  src="/Assets/logota.jpg"
                  sx={{ height: 44, objectFit: "contain" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: 900,
                  color: "#0f172a",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                }}
              >
                Sign in
              </Typography>
              <Typography
                sx={{ fontSize: 14, color: "#64748b", mb: 4, lineHeight: 1.6 }}
              >
                Enter your mobile number and we'll send you an OTP
              </Typography>

              <Box component="form" onSubmit={handleLogin}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#374151",
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Mobile Number
                </Typography>

                <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                  <TextField
                    fullWidth
                    placeholder="mobile number"
                    value={phone}
                    autoComplete="off"
                    onChange={(e) => {
                      const n = e.target.value.replace(/\D/g, "");
                      if (n.length <= 10) setPhone(n);
                    }}
                    error={error}
                    helperText={
                      error ? "Enter valid number starting with 6-9" : ""
                    }
                    inputProps={{ maxLength: 10, inputMode: "numeric" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            sx={{
                              bgcolor: "#f0fdf4",
                              px: 1.2,
                              py: 0.5,
                              borderRadius: "7px",
                              color: "#16a34a",
                              fontWeight: 700,
                              fontSize: 13,
                              whiteSpace: "nowrap",
                            }}
                          >
                            🇮🇳 +91
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#f8fafc",
                        borderRadius: "12px",
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || cooldown > 0}
                    sx={{
                      px: 2.5,
                      minWidth: 96,
                      borderRadius: "12px",
                      whiteSpace: "nowrap",
                      fontWeight: 700,
                      background:
                        loading || cooldown > 0
                          ? "#e2e8f0"
                          : "linear-gradient(135deg, #16a34a, #22c55e)",
                      color: loading || cooldown > 0 ? "#94a3b8" : "white",
                      boxShadow:
                        loading || cooldown > 0
                          ? "none"
                          : "0 4px 14px rgba(22,163,74,0.3)",
                    }}
                  >
                    {loading
                      ? "..."
                      : cooldown > 0
                        ? `${cooldown}s`
                        : "Get OTP"}
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => (window.location.href = frontbaseapi)}
                    sx={{
                      color: "#94a3b8",
                      fontSize: 12,
                      p: 0,
                      "&:hover": { color: "#64748b" },
                    }}
                  >
                    ← Back to site
                  </Button>
                  <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                    New user?{" "}
                    <Box
                      component="span"
                      onClick={() => navigate("/registration")}
                      sx={{
                        color: "#16a34a",
                        fontWeight: 700,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Register free
                    </Box>
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#94a3b8",
                    textAlign: "center",
                    lineHeight: 1.7,
                  }}
                >
                  By continuing you agree to our{" "}
                  <Link
                    component={RouterLink}
                    to="/termsandconditions"
                    style={{
                      color: "#16a34a",
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link
                    component={RouterLink}
                    to="/termsandconditions"
                    style={{
                      color: "#16a34a",
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 3,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {["RBI Registered", "BBPS Certified", "ISO 27001"].map((l) => (
                <Chip
                  key={l}
                  label={l}
                  size="small"
                  sx={{
                    bgcolor: "white",
                    fontSize: 11,
                    color: "#64748b",
                    border: "1px solid #e2e8f0",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Loginpage;